import { ResizeNotifier } from '@theme/critical';

/**
 * Equalize collection-card button heights per grid row (issue #178).
 *
 * Grid collection cards whose button labels wrap to a different number of
 * lines end up with staggered buttons. CSS cannot size a button to match its
 * tallest sibling in another grid cell (the on-image placement renders the
 * button inside an absolutely-positioned overlay, so no flex/grid
 * relationship exists between cards), so this script measures the buttons in
 * each visual row and applies the tallest natural height as min-height.
 *
 * Only buttons with the "Allow text to wrap to multiple lines" setting
 * enabled are targeted (that setting renders the `.button--wrap` class);
 * without it labels ellipsize on one line and heights already match.
 */

const GRID_SELECTOR = '.resource-list--grid';
const BUTTON_SELECTOR = '.collection-card .button--wrap';

/** @type {WeakSet<Element>} */
const observedGrids = new WeakSet();

const resizeNotifier = new ResizeNotifier(() => scheduleEqualize());

let scheduled = false;

function scheduleEqualize() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    equalizeAll();
  });
}

function equalizeAll() {
  for (const grid of document.querySelectorAll(GRID_SELECTOR)) {
    /** @type {HTMLElement[]} */
    const buttons = Array.from(grid.querySelectorAll(BUTTON_SELECTOR));
    if (buttons.length === 0) continue;

    if (!observedGrids.has(grid)) {
      observedGrids.add(grid);
      resizeNotifier.observe(grid);
    }

    for (const button of buttons) button.style.minHeight = '';

    // The mobile/desktop fallback list is display: none; skip it until its
    // grid reports a size again (the ResizeNotifier fires on that change).
    if (grid.getBoundingClientRect().width === 0) continue;

    // Group buttons into visual rows by their card's top edge, then apply
    // the tallest natural height in each row to the whole row.
    /** @type {Map<number, HTMLElement[]>} */
    const rows = new Map();
    for (const button of buttons) {
      const item = button.closest('.resource-list__item') ?? button;
      const top = Math.round(item.getBoundingClientRect().top);
      const row = rows.get(top) ?? [];
      row.push(button);
      rows.set(top, row);
    }

    for (const row of rows.values()) {
      const max = Math.max(...row.map((button) => button.getBoundingClientRect().height));
      for (const button of row) button.style.minHeight = `${max}px`;
    }
  }
}

function init() {
  equalizeAll();
  // Web fonts change label metrics; remeasure once they are in.
  document.fonts?.ready.then(scheduleEqualize);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', scheduleEqualize);
  document.addEventListener('shopify:block:select', scheduleEqualize);
}
